/**
 * Error types for categorizing different kinds of errors
 */
export enum ErrorType {
  CLIENT_ERROR = "CLIENT_ERROR",
  SERVER_ERROR = "SERVER_ERROR",
  NETWORK_ERROR = "NETWORK_ERROR",
  VALIDATION_ERROR = "VALIDATION_ERROR",
  APPLICATION_ERROR = "APPLICATION_ERROR",
  TIMEOUT_ERROR = "TIMEOUT_ERROR",
  AUTHENTICATION_ERROR = "AUTHENTICATION_ERROR",
  AUTHORIZATION_ERROR = "AUTHORIZATION_ERROR",
  NOT_FOUND_ERROR = "NOT_FOUND_ERROR",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
  API_ERROR = "API_ERROR",
}

/**
 * Toast notification severity levels
 */
export enum ToastSeverity {
  ERROR = "error",
  WARNING = "warning",
  INFO = "info",
  SUCCESS = "success",
}

/**
 * Error codes mapping to user-friendly messages
 */
export const ERROR_CODES = {
  // Network errors (1xx)
  100: "Connection lost. Please check your internet connection and try again.",
  101: "Request timeout. The server is taking too long to respond.",
  102: "Unable to connect to the server. Please try again later.",

  // Client errors (4xx)
  400: "Invalid request. Please check your input and try again.",
  401: "Your session has expired. Please log in again.",
  403: "You do not have permission to perform this action.",
  404: "The requested information could not be found.",
  408: "Request timeout. Please try again.",
  422: "Invalid data provided. Please check your input.",
  429: "Too many requests. Please wait a moment and try again.",

  // Server errors (5xx)
  500: "An internal server error occurred. Please try again later or contact support.",
  502: "Service temporarily unavailable. Please try again later.",
  503: "Service is currently undergoing maintenance. Please try again later.",
  504: "Gateway timeout. The server is not responding.",

  // Custom application errors (9xx)
  900: "An unexpected error occurred. Please try again.",
  901: "Failed to save data. Please try again.",
  902: "Failed to load data. Please refresh the page.",
  903: "Form validation failed. Please check your input.",
  904: "Operation cancelled by user.",
  905: "File upload failed. Please try again.",
} as const;

/**
 * Default error messages for different error types
 */
export const DEFAULT_ERROR_MESSAGES = {
  [ErrorType.CLIENT_ERROR]:
    "There was an issue with your request. Please check your input and try again.",
  [ErrorType.SERVER_ERROR]:
    "A server error occurred. Please try again later or contact support if the problem persists.",
  [ErrorType.NETWORK_ERROR]:
    "Network connection lost. Please check your internet connection and try again.",
  [ErrorType.VALIDATION_ERROR]:
    "Please check your input and correct any errors before continuing.",
  [ErrorType.APPLICATION_ERROR]:
    "An unexpected error occurred. Please try again or contact support if the problem persists.",
  [ErrorType.TIMEOUT_ERROR]: "The request timed out. Please try again.",
  [ErrorType.AUTHENTICATION_ERROR]:
    "Authentication failed. Please log in again.",
  [ErrorType.AUTHORIZATION_ERROR]:
    "You do not have permission to perform this action.",
  [ErrorType.NOT_FOUND_ERROR]: "The requested resource could not be found.",
  [ErrorType.UNKNOWN_ERROR]:
    "An unknown error occurred. Please try again or contact support.",
  [ErrorType.API_ERROR]:
    "An API error occurred. Please try again or contact support.",
} as const;

/**
 * Interface for structured error information
 */
export interface AppError {
  id: string;
  type: ErrorType;
  code?: number;
  message: string;
  originalError?: Error | unknown;
  timestamp: Date;
  context?: Record<string, unknown>;
  severity: ToastSeverity;
  dismissible?: boolean;
  autoHideDuration?: number;
  actionLabel?: string;
  onAction?: () => void;
}

/**
 * Interface for toast notification state
 */
export interface ToastNotification {
  id: string;
  message: string;
  severity: ToastSeverity;
  dismissible?: boolean;
  autoHideDuration?: number;
  autoClose?: number | false; // For react-toastify compatibility
  progress?: number; // For react-toastify progress
  hideProgressBar?: boolean;
  closeOnClick?: boolean;
  pauseOnHover?: boolean;
  draggable?: boolean;
  className?: string;
  actionLabel?: string;
  onAction?: () => void;
}

/**
 * Error context state interface
 */
export interface ErrorContextState {
  errors: AppError[];
  toasts: ToastNotification[];
}

/**
 * Success message types for different operations
 */
export const SUCCESS_MESSAGES = {
  SAVE: "Data saved successfully.",
  UPDATE: "Data updated successfully.",
  DELETE: "Data deleted successfully.",
  CREATE: "Data created successfully.",
  SUBMIT: "Form submitted successfully.",
  UPLOAD: "File uploaded successfully.",
} as const;
